import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  styled,
  useTheme,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';

// Original Images aus Desktop Version
import HaefniImage from "assets/logos/haefni2.jpg";
import KristjanImage from "assets/logos/kristjan_image.png";
import GlodisImage from "assets/logos/glodis_image.png";
import CorporateImage from "assets/logos/corporate_learning_coach_image.png";

import GermanyFlag from "assets/icons/de_flag.png";
import UKFlag from "assets/icons/us_uk_flag.png";
import SpainFlag from "assets/icons/es_flag.png";
import TurkeyFlag from "assets/icons/tr_flag.png";
import PolandFlag from "assets/icons/pl_flag.png";
import ItalyFlag from "assets/icons/it_flag.png";
import FranceFlag from "assets/icons/fr_flag.png";

// Praxisfokus Images
import grafik_aufgabenbereich from "assets/images/grafik_aufgabenbereich.png";
import grafik_branche from "assets/images/grafik_branche.png";

// Mobile UseCase Selector Card Import
import MobileUseCaseSelectorCard from './MobileUseCaseSelectorCard';

// Original Assistants Data
const assistants = [
  {
    id: 1,
    name: "Hæfni",
    img: HaefniImage,
    description: {
      en: "Hæfni gives you examples and explanations, tells vivid little stories from the business world, humorous.",
      de: "Gibt dir Beispiele und Erläuterungen, erzählt anschaulich kleine Stories aus der Businesswelt, humorvoll.",
    },
    form: {
      en: "She uses a casual speech style",
      de: "Verwendet die Du-Form",
    },
    available: true,
    activeLanguages: [GermanyFlag],
  },
  {
    id: 2,
    name: "Kristján",
    img: KristjanImage,
    description: {
      en: "He uses a rather formal speech style",
      de: "Vermittelt Ihnen die wichtigsten Tipps & Tricks kurz und knapp, schnörkellos auf den Punkt.",
    },
    form: {
      en: "He uses a rather formal speech style. ",
      de: "Verwendet die Sie-Form",
    },
    available: false,
    activeLanguages: [GermanyFlag],
  },
  {
    id: 3,
    name: "Glódís",
    img: GlodisImage,
    description: {
      en: "Glódís explains the best tips briefly and clearly. Some details and topics are omitted.",
      de: "Formuliert verständlich in einfacher Sprache. Einige Details und Themen werden weggelassen.",
    },
    form: {
      en: "She uses a simple language. ",
      de: "Verwendet die Du-Form",
    },
    available: false,
    activeLanguages: [GermanyFlag],
  },
  {
    id: 4,
    name: "Corporate",
    img: CorporateImage,
    description: {
      en: "The avatar and look & feel of the chatbot can be customized according to your corporate design.",
      de: "Avatar und Look & Feel des Chatbots kann nach Ihrem Corporate Design ausgestattet werden.",
    },
    form: {
      en: "",
      de: "Du oder Sie-Form",
    },
    available: false,
    activeLanguages: [GermanyFlag],
  },
];

const languages = [
  { flag: GermanyFlag, active: true },
  { flag: UKFlag, active: false },
  { flag: SpainFlag, active: false },
  { flag: ItalyFlag, active: false },
  { flag: PolandFlag, active: false },
  { flag: TurkeyFlag, active: false },
  { flag: FranceFlag, active: false },
];

// Styled Components
const Container = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
  width: '100%',
}));

const AvatarCard = styled(Card)<{ selected?: boolean; isComingSoon?: boolean }>(({ theme, selected, isComingSoon }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
  border: selected ? '4px solid #a1b7a5' : '2px solid #e0e0e0',
  transition: 'all 0.3s ease',
  position: 'relative',
  filter: isComingSoon ? 'grayscale(100%)' : 'none',
  cursor: isComingSoon ? 'not-allowed' : 'pointer',
  '&:hover': {
    transform: isComingSoon ? 'none' : 'translateY(-2px)',
    boxShadow: isComingSoon ? '0 4px 10px rgba(0, 0, 0, 0.1)' : '0 6px 18px rgba(0, 0, 0, 0.1)',
  },
}));

const ComingSoonLabel = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  top: '1.5rem',
  left: '50%',
  transform: 'translateX(-50%)',
  color: '#e53935',
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  zIndex: 10,
}));

const FavoriteButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '12px',
  right: '12px',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(8px)',
  width: '40px',
  height: '40px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease',
  zIndex: 10,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    transform: 'scale(1.1)',
  },
}));

const AvatarImageContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: '1.5rem',
  marginBottom: '1rem',
}));

const AvatarImage = styled('img')(() => ({
  width: '90px',
  height: '90px',
  objectFit: 'contain',
}));

const AvatarName = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 'bold',
  textAlign: 'center',
  letterSpacing: '0.05em',
  marginBottom: '0.5rem',
}));

const AvatarDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  textAlign: 'center',
  color: '#333',
  padding: '0 1rem',
  marginBottom: '0.75rem',
  lineHeight: 1.4,
}));

const FormText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 'bold',
  color: '#4A4A4A',
  textAlign: 'center',
  marginBottom: '1rem',
}));

const LanguageContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  gap: '8px',
  alignItems: 'center',
  padding: '0 12px',
  marginBottom: '1rem',
}));

const LanguageCircle = styled(Box)<{ active?: boolean }>(({ active }) => ({
  width: '22px',
  height: '22px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  backgroundColor: active ? 'transparent' : '#d3d3d3',
  filter: active ? 'none' : 'grayscale(100%)',
  overflow: 'hidden',
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
}));

// Praxisfokus Section
const PraxisFokusSection = styled(Box)(({ theme }) => ({
  marginTop: '2rem',
}));

const PraxisFokusTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 700,
  color: '#002E5A',
  textAlign: 'center',
  marginBottom: '1.5rem',
}));

const FokusGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '1rem',
}));

const StyledButton = styled(Button)(() => ({
  marginTop: '2rem',
  padding: '0.75rem 2rem',
  backgroundColor: '#a1b7a5',
  color: 'white',
  fontSize: '1.1rem',
  fontWeight: 'bold',
  textTransform: 'none',
  borderRadius: '8px',
  alignSelf: 'center',
  '&:hover': {
    backgroundColor: '#8fa895',
  },
}));

// Component
const MobileAvatarSelectionCard = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  
  const [selectedAssistant, setSelectedAssistant] = useState<number | null>(null);
  const [favorites, setFavorites] = useState<Set<number>>(new Set());

  useEffect(() => {
    const storedAssistant = localStorage.getItem("selectedAssistant");
    if (storedAssistant) {
      const assistantId = parseInt(storedAssistant, 10);
      const selected = assistants.find(a => a.id === assistantId);
      if (selected) {
        setSelectedAssistant(selected.id);
      } else {
        setSelectedAssistant(assistants[0].id);
        localStorage.setItem("selectedAssistant", String(assistants[0].id));
        localStorage.setItem("selectedAvatar", assistants[0].img);
      }
    } else {
      setSelectedAssistant(assistants[0].id);
      localStorage.setItem("selectedAssistant", String(assistants[0].id));
      localStorage.setItem("selectedAvatar", assistants[0].img);
    }
  }, []);

  const toggleFavorite = (assistantId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(assistantId)) {
        newFavorites.delete(assistantId);
      } else {
        newFavorites.add(assistantId);
      }
      return newFavorites;
    });
  };

  const handleSelect = (id: number) => {
    const selected = assistants.find(a => a.id === id);
    if (!selected || !selected.available) return;
    if (selectedAssistant === id) return;

    setSelectedAssistant(id);
    localStorage.setItem("selectedAssistant", String(id));
    localStorage.setItem("selectedAvatar", selected.img);
  };

  const renderAssistantName = (assistant: typeof assistants[0]) => {
    if (assistant.name === "Hæfni") {
      return (
        <>
          <span style={{ color: "#002e5a" }}>Hæf</span>
          <span style={{ color: "#D72638" }}>ni</span>
        </>
      );
    } else if (assistant.name === "Kristján") {
      return <span style={{ color: "#002e5a" }}>{assistant.name}</span>;
    } else if (assistant.name === "Glódís") {
      return (
        <>
          <span style={{ color: "#002e5a" }}>Gló</span>
          <span style={{ color: "#D72638" }}>dís</span>
        </>
      );
    } else {
      return (
        <Box display="flex" flexDirection="column" alignItems="center">
          <span style={{ color: "#D72638" }}>Corporate</span>
          <span style={{ color: "#002e5a" }}>Lerncoach</span>
        </Box>
      );
    }
  };

  const handleNavigateToSkillary = () => {
    navigate("/skillary");
  };

  return (
    <Container>
      {/* Avatar Cards */}
      {assistants.map((assistant) => (
        <AvatarCard
          key={assistant.id}
          selected={selectedAssistant === assistant.id}
          isComingSoon={!assistant.available}
          onClick={() => handleSelect(assistant.id)}
        >
          {!assistant.available && (
            <ComingSoonLabel>
              {t("Coming Soon")}
            </ComingSoonLabel>
          )}

          <FavoriteButton 
            onClick={(e) => toggleFavorite(assistant.id, e)}
            size="small"
          >
            {favorites.has(assistant.id) ? (
              <FavoriteIcon sx={{ color: theme.palette.error.main, fontSize: '1.2rem' }} />
            ) : (
              <FavoriteBorderIcon sx={{ color: theme.palette.text.secondary, fontSize: '1.2rem' }} />
            )}
          </FavoriteButton>

          <CardContent sx={{ padding: '1rem' }}>
            <AvatarImageContainer>
              <AvatarImage src={assistant.img} alt={assistant.name} />
            </AvatarImageContainer>

            <AvatarName>
              {renderAssistantName(assistant)}
            </AvatarName>

            <AvatarDescription>
              {assistant.description[i18n.language as "en" | "de"]}
            </AvatarDescription>

            <FormText>
              {assistant.form[i18n.language as "en" | "de"]}
            </FormText>

            <LanguageContainer>
              {languages.map((lang, index) => {
                const isActive = assistant.activeLanguages.includes(lang.flag);
                return (
                  <LanguageCircle key={index} active={isActive}>
                    <img
                      src={lang.flag}
                      alt="flag"
                      width="100%"
                      height="100%"
                      style={{
                        objectFit: "cover",
                        borderRadius: "50%",
                        filter: isActive ? "none" : "grayscale(100%)",
                        opacity: isActive ? "1" : "0.5",
                      }}
                    />
                  </LanguageCircle>
                );
              })}
            </LanguageContainer>
          </CardContent>
        </AvatarCard>
      ))}

      {/* Praxisfokus Section */}
      <PraxisFokusSection>
        <PraxisFokusTitle>
          {t("selectUseCaseDescription")}
        </PraxisFokusTitle>
        
        <FokusGrid>
          <MobileUseCaseSelectorCard
            id="usecase-function"
            title={t("Function Selection")}
            imageSrc={grafik_aufgabenbereich}
            options={["Others", "Sales", "Marketing", "Accounting", "HR", "Procurement", "IT"]}
            defaultValue="Others"
          />
          
          <MobileUseCaseSelectorCard
            id="usecase-industry"
            title={t("Industry Selection")}
            imageSrc={grafik_branche}
            options={[
              "Software/ICT", "Engineering", "Chemistry/Pharma",
              "Banking/Insurance", "Logistics", "Retail", "Energy"
            ]}
            defaultValue="Software/ICT"
          />
        </FokusGrid>
      </PraxisFokusSection>
    </Container>
  );
};

export default MobileAvatarSelectionCard;